import { useEffect, useRef } from "react";
import { ArrowRight, CalendarClock } from "lucide-react";
import { FadeUp } from "@/components/Reveal";
import { SCHEDULER_URL, isRealSchedulerUrl } from "@/lib/site";

const SCRIPT_ID = "calendly-widget-script";
const SCRIPT_SRC = "https://assets.calendly.com/assets/external/widget.js";

export const SchedulerEmbed = () => {
  const containerRef = useRef(null);
  const configured = isRealSchedulerUrl(SCHEDULER_URL);

  useEffect(() => {
    if (!configured || !containerRef.current) return;
    if (!document.getElementById(SCRIPT_ID)) {
      const script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.src = SCRIPT_SRC;
      script.async = true;
      document.body.appendChild(script);
    }
  }, [configured]);

  return (
    <section data-testid="scheduler-section" className="border-t border-black/10 bg-white py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <FadeUp>
          <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.25em] text-pine">
            <span className="h-px w-10 bg-pine" />
            Instant scheduling
          </p>
          <h2 className="mt-6 max-w-2xl font-serif text-4xl leading-tight tracking-tighter text-ink sm:text-5xl">
            Pick a time, <em className="text-pine">right now.</em>
          </h2>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-zinc-600">
            Choose a slot that suits you and it lands straight on our calendar — no back-and-forth
            emails required.
          </p>
        </FadeUp>

        <FadeUp delay={0.1}>
          {configured ? (
            <div className="mt-12 overflow-hidden rounded-3xl border border-black/10 bg-paper">
              <div
                ref={containerRef}
                data-testid="calendly-inline-widget"
                className="calendly-inline-widget w-full"
                data-url={SCHEDULER_URL}
                style={{ minWidth: "320px", height: "700px" }}
                aria-label="Schedule a meeting with CalCor Solutions"
              />
            </div>
          ) : (
            <div
              data-testid="scheduler-fallback-card"
              className="mt-12 flex flex-col items-start gap-5 rounded-3xl border border-dashed border-pine/40 bg-paper p-10 sm:p-12"
            >
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-pine/10">
                <CalendarClock className="h-6 w-6 text-pine" />
              </span>
              <h3 className="font-serif text-2xl tracking-tight text-ink">
                The live calendar connects here.
              </h3>
              <p className="max-w-xl text-sm leading-relaxed text-zinc-600 sm:text-base">
                Once the firm&rsquo;s scheduling calendar is linked, visitors will book confirmed
                appointments without leaving this page. Until then, the booking form above collects
                your preferred time and we confirm within one business day.
              </p>
              <button
                type="button"
                data-testid="scheduler-fallback-booking-button"
                onClick={() => {
                  const trigger = document.querySelector('[data-testid="tab-booking"]');
                  if (trigger) {
                    trigger.dispatchEvent(new MouseEvent("mousedown", { bubbles: true, cancelable: true, button: 0 }));
                    trigger.click();
                  }
                  document.querySelector('[data-testid="contact-tabs"]')?.scrollIntoView({ behavior: "smooth" });
                }}
                className="group inline-flex items-center gap-2 rounded-full bg-pine px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-clay"
              >
                Request an appointment instead
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
          )}
        </FadeUp>
      </div>
    </section>
  );
};
